/**
 * 合成圖標記(`composite`)— ADR-057 / BE docs/140 §4.2
 * -----------------------------------------------------------------------------
 * 前端把幾張參考圖合成一張(1×2 / 2×2 / 3×3,圖上沒有任何文字)上傳時帶這個標記;
 * 後端存進上傳紀錄 → 畫布素材 `metadata.composite` / 素材庫 item 頂層 `composite`,
 * 用來①告訴模型「這是同一個主體的多角度參考,不要照著拼貼」②讓 @ 可以指到某一格
 * ③「拆回」時換回原本那幾張。
 *
 * wire 形狀(snake):`{ layout: "2x2", cells: [{ row, col, asset_id }] }`,row / col 從 1 起算,
 * `asset_id` = 那一格的**原圖**(上傳紀錄 id 或專案素材 id)。
 *
 * 讀取方向的紀律(R296:wire 值域是開放的):
 *   - `layout` **不用封閉 union 過濾** —— 讀成字串,版面列數 / 欄數由 `"RxC"` 解析。BE 日後
 *     加新版面,舊 FE 照樣認得出「這是合成圖、裡面有哪幾格」。
 *   - 形狀壞掉(不是物件 / cells 不是陣列 / 某格缺欄)→ **整個當 null**。失敗的方向只能是
 *     「當成一張普通圖片」(BE 自己對素材庫路徑也是這樣退化:不出錯,只是沒有多角度
 *     說明句、不能 @ 某一格),**不可以**留下半套 cells 讓「拆回」只換回其中幾張。
 *
 * 送出方向:只送 FE 合成工具做得出來的三種版面({@link COMPOSITE_LAYOUTS}),送前先過
 * {@link compositeSpecProblem}(BE 422 `UPLOAD_COMPOSITE_INVALID` 才是權威)。
 */

/** FE 合成工具做得出來、也會送出的版面(BE catalog `composite_layouts` 現值同三種)。 */
export const COMPOSITE_LAYOUTS = ['1x2', '2x2', '3x3'] as const;
export type CompositeLayout = (typeof COMPOSITE_LAYOUTS)[number];

export interface CompositeCell {
  /** 第幾列(1 起算)。 */
  row: number;
  /** 第幾欄(1 起算)。 */
  col: number;
  /** 這一格的原圖 id(上傳紀錄 id 或專案素材 id)。 */
  assetId: string;
}

export interface CompositeSpec {
  /** 讀回來的版面(開放字串,見檔頭);FE 送出的一定是 {@link CompositeLayout}。 */
  layout: string;
  /** 依閱讀順序(BE 存的一律是這個順序;FE 讀回來也照這個順序排)。 */
  cells: CompositeCell[];
}

export interface CompositeCellDto {
  row: number;
  col: number;
  asset_id: string;
}

export interface CompositeSpecDto {
  layout: string;
  cells: CompositeCellDto[];
}

/** `"2x2"` → `{ rows: 2, cols: 2 }`;不是 `RxC` 形狀(或任一邊 < 1)→ null。 */
export function compositeGrid(layout: string): { rows: number; cols: number } | null {
  const m = /^(\d+)x(\d+)$/.exec(layout);
  if (!m) return null;
  const rows = Number(m[1]);
  const cols = Number(m[2]);
  return rows >= 1 && cols >= 1 ? { rows, cols } : null;
}

function isPositiveInt(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= 1;
}

/**
 * wire → FE。形狀壞掉一律回 null(= 當成一張普通圖片,見檔頭)。
 *
 * 只驗「讀得懂」(物件形狀 + 版面解析得出列欄 + 每格位置在版面內、id 非空);
 * 「格數下限 / 重複」這類送出規則是 BE 寫入時的事,讀回來不再擋。
 */
export function mapCompositeDto(raw: unknown): CompositeSpec | null {
  if (!raw || typeof raw !== 'object') return null;
  const { layout, cells } = raw as { layout?: unknown; cells?: unknown };
  if (typeof layout !== 'string' || !Array.isArray(cells) || cells.length === 0) return null;
  const grid = compositeGrid(layout);
  if (!grid) return null;
  const out: CompositeCell[] = [];
  for (const c of cells) {
    if (!c || typeof c !== 'object') return null;
    const { row, col, asset_id } = c as { row?: unknown; col?: unknown; asset_id?: unknown };
    if (!isPositiveInt(row) || !isPositiveInt(col) || row > grid.rows || col > grid.cols) return null;
    if (typeof asset_id !== 'string' || asset_id === '') return null;
    out.push({ row, col, assetId: asset_id });
  }
  out.sort((a, b) => a.row - b.row || a.col - b.col);
  return { layout, cells: out };
}

/** FE → wire(只在真的要送時呼叫;送前先過 {@link compositeSpecProblem})。 */
export function compositeToDto(spec: CompositeSpec): CompositeSpecDto {
  return {
    layout: spec.layout,
    cells: spec.cells.map((c) => ({ row: c.row, col: c.col, asset_id: c.assetId })),
  };
}

/** {@link compositeSpecProblem} 的回傳值(對應 BE `details.reason` 的各條,FE 自己的分類)。 */
export type CompositeSpecProblem =
  | 'layout'
  | 'cellCount'
  | 'cellOutOfRange'
  | 'duplicateCell'
  | 'duplicateAsset'
  | 'assetIdFormat';

/**
 * 原圖 id 的格式(鏡射 BE `app/uploads/composite._CELL_ID_RE` + `max_length=100`;
 * dev-update-v22-222 取證)。上傳紀錄 id / 專案素材 id 都符合;不符 = BE 422。
 */
const CELL_ASSET_ID_RE = /^[A-Za-z0-9_-]{1,100}$/;

/**
 * 送出前自驗(鏡射 docs/140 §4.2 + BE `CompositeSpec` 原始碼):版面 ∈ 三種 / 2 ≤ 格數 ≤
 * 版面格數 / 位置在版面內 / 同一格不重複 / 原圖 id 不重複、而且只含英數 `_` `-`、長度 1–100
 * (最後這條契約文件沒寫,是讀 BE dev-update-v22-222 原始碼才看到的)。合法回 null。
 *
 * ⚠️ 這是**前擋**不是權威:BE 422 `UPLOAD_COMPOSITE_INVALID` 才是。FE 比 BE 嚴可以,
 * 反過來(放行 BE 會擋的)= 上傳到一半才 422。
 */
export function compositeSpecProblem(spec: CompositeSpec): CompositeSpecProblem | null {
  if (!(COMPOSITE_LAYOUTS as readonly string[]).includes(spec.layout)) return 'layout';
  const grid = compositeGrid(spec.layout);
  if (!grid) return 'layout';
  const n = spec.cells.length;
  if (n < 2 || n > grid.rows * grid.cols) return 'cellCount';
  const positions = new Set<string>();
  const ids = new Set<string>();
  for (const c of spec.cells) {
    if (!isPositiveInt(c.row) || !isPositiveInt(c.col) || c.row > grid.rows || c.col > grid.cols) {
      return 'cellOutOfRange';
    }
    if (!CELL_ASSET_ID_RE.test(c.assetId)) return 'assetIdFormat';
    const pos = `${c.row}:${c.col}`;
    if (positions.has(pos)) return 'duplicateCell';
    positions.add(pos);
    if (ids.has(c.assetId)) return 'duplicateAsset';
    ids.add(c.assetId);
  }
  return null;
}

/**
 * 張數 → 版面(docs/140 §4.1 建議選法:2 張 1×2 / 3–4 張 2×2 / 5–9 張 3×3)。
 * 少於 2 或多於 9 → null(合成不了;呼叫端不該給這種組)。
 */
export function layoutForCount(n: number): CompositeLayout | null {
  if (n === 2) return '1x2';
  if (n >= 3 && n <= 4) return '2x2';
  if (n >= 5 && n <= 9) return '3x3';
  return null;
}

/**
 * 依閱讀順序把原圖 id 排進版面(第一張 = 左上,往右再往下)—— 與合成工具畫格子的順序
 * 是同一份(合成工具 `planComposite` 吃同一個 cols)。兩邊各算一次 = 格子畫的是 A、標記寫的是 B,
 * 「@ 某一格」就指錯圖,而畫面完全正常。
 */
export function cellsForAssets(layout: CompositeLayout, assetIds: readonly string[]): CompositeCell[] {
  const grid = compositeGrid(layout)!;
  return assetIds.map((assetId, i) => ({
    row: Math.floor(i / grid.cols) + 1,
    col: (i % grid.cols) + 1,
    assetId,
  }));
}
